/**
 * HX Finance Command — Data & Builders
 * --------------------------------------------------------------------------
 * Extracted from `FinanceCommandDashboard.jsx` to reduce file size and
 * separate static configuration from component logic. Importing this file
 * has zero side effects; all exports are pure data or pure functions.
 *
 * Conventions:
 *   - Data constants are UPPER_SNAKE_CASE
 *   - Builders/derivers (use real state with demo fallback) are `buildX()` / `deriveX()`
 *   - Date helpers are tiny pure utilities (`toIso`, `isSameDay`, `formatPtDate`)
 */

import {
  Home, BookOpen, LayoutGrid, ChartArea, Target, Map, Info,
  ShieldCheck, Heart, GraduationCap, Car, Plane, Briefcase,
  Calendar, ClipboardList, BarChart3, Settings, Lock,
  Wallet, Receipt, PiggyBank, TrendingUp,
  Sun, Sunrise, Sunset, Moon, Circle,
  Flag, Star, Trophy, Sparkles, Users, Globe,
} from "lucide-react";

/* ===================== KPIs (demo fallback) ===================== */
export const KPIS = [
  { label: "Rendimento Mensal",         value: "2.850", unit: "€", sub: "Após Impostos",       delta: "+12% vs mês anterior", Icon: Wallet,     accent: "neon" },
  { label: "Despesa Total",             value: "1.650", unit: "€", sub: "Este Mês",            delta: "58% do rendimento",    Icon: Receipt,    accent: "red"  },
  { label: "Taxa de Poupança",          value: "42",    unit: "%", sub: "Este Mês",            delta: "Excelente",            Icon: PiggyBank,  accent: "neon" },
  { label: "Reserva de Emergência",     value: "2.650", unit: "€", sub: "do Alvo de 5.400 €",  delta: "49% Concluído",        Icon: ShieldCheck,accent: "neon" },
  { label: "Investido em HX Ecosystems", value: "1.250", unit: "€", sub: "Este Mês",           delta: "+15% vs mês anterior", Icon: TrendingUp, accent: "neon" },
];

/* ===================== Calendar helpers ===================== */
export const PT_MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
export const PT_MONTHS_FULL = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
export const CURRENT_MONTH_LABEL = PT_MONTHS_FULL[new Date().getMonth()];

export const DOW_MAP = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const PT_DOW_SHORT = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
export const WEEK_START_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday-first

export const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
export const isSameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
export const formatPtDate = (iso) => {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  } catch { return iso; }
};

/* ===================== Cash flow (6 months, anchored to current) ===================== */
export const CASH_FLOW = (() => {
  const now = new Date();
  const startMonth = now.getMonth();
  const startYear = now.getFullYear();
  const PATTERN = [
    { income: 2850, expenses: 1650, savings: 1200 },
    { income: 2900, expenses: 1700, savings: 1200 },
    { income: 2950, expenses: 1680, savings: 1270 },
    { income: 3000, expenses: 1720, savings: 1280 },
    { income: 3100, expenses: 1750, savings: 1350 },
    { income: 3200, expenses: 1780, savings: 1420 },
  ];
  return PATTERN.map((p, i) => {
    const idx = (startMonth + i) % 12;
    const year = startYear + Math.floor((startMonth + i) / 12);
    return { m: `${PT_MONTHS_SHORT[idx]}${i === 0 ? ` ${String(year).slice(2)}` : ""}`, ...p };
  });
})();

/* ===================== Budget donut ===================== */
export const BUDGET_SLICES_DEF = [
  { key: "family",      k: "Família",         color: "#0A9B5F" },
  { key: "faith",       k: "Fé & Dízimo",     color: "#EAB308" },
  { key: "emergency",   k: "Reserva",         color: "#0A9B5F" },
  { key: "growth",      k: "Crescimento",     color: "#A855F7" },
  { key: "driving",     k: "Mobilidade",      color: "#F97316" },
  { key: "experiences", k: "Experiências",    color: "#06B6D4" },
  { key: "hx",          k: "HX Ecosystems",   color: "#EF4444" },
];
export const BUDGET_DEMO = {
  family: 1250, faith: 285, emergency: 350, growth: 320, driving: 120, experiences: 100, hx: 1250,
};

export const buildBudgetSlices = (entriesByCat, unlocked) => {
  return BUDGET_SLICES_DEF.map((s) => {
    const list = entriesByCat?.[s.key] || [];
    const sum = list.reduce((acc, e) => acc + (e.amount || 0), 0);
    const v = (unlocked && sum > 0) ? sum : (unlocked ? sum : BUDGET_DEMO[s.key]);
    return { ...s, v };
  }).filter((s) => s.v > 0);
};

/* ===================== Roadmap & reminders & goals & shifts ===================== */
export const ROADMAP = [
  { n:  1, label: "Fé",                                status: "Sempre",              done: true  },
  { n:  2, label: "Estabilidade Familiar",             status: "Contínuo",            done: true  },
  { n:  3, label: "Reserva de Emergência",             status: "Em Curso",            done: true  },
  { n:  4, label: "Integração Linguística (Alemão)",   status: "Em Curso",            done: true  },
  { n:  5, label: "Ausbildung",                        status: "Próxima Prioridade",  done: false, next: true },
  { n:  6, label: "Crescimento Profissional",          status: "",                    done: false },
  { n:  7, label: "Carta de Condução",                 status: "",                    done: false },
  { n:  8, label: "Desenvolvimento HX Ecosystems",     status: "",                    done: false },
  { n:  9, label: "Expansão Estratégica",              status: "",                    done: false },
  { n: 10, label: "Legado",                            status: "",                    done: false },
];

export const REMINDERS = (() => {
  const now = new Date();
  const make = (offsetDays, label, color) => {
    const d = new Date(now); d.setDate(d.getDate() + offsetDays);
    const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return { label, date: `${String(d.getDate()).padStart(2,"0")} ${PT_MONTHS_SHORT[d.getMonth()]}`, due_date: iso, color };
  };
  return [
    make( 5, "Reunião com Parceiro",             "#EF4444"),
    make(10, "Curso de Alemão",                  "#0A9B5F"),
    make(20, "Compromisso de Negócios",          "#F97316"),
    make(25, "Pagamento de Dízimo",              "#0A9B5F"),
    make(28, "Contribuição Fundo de Emergência", "#EAB308"),
    make(40, "Planeamento Viagem Familiar",      "#A855F7"),
  ];
})();

export const TOP_GOALS = [
  { label: "Fundo de Emergência (5.400 €)", pct: 49 },
  { label: "Alemão B1",                     pct: 70 },
  { label: "Início da Ausbildung",          pct: 20 },
  { label: "Carta de Condução",             pct: 10 },
  { label: "Lançamento HX Smart Point",     pct: 45 },
];

export const CATEGORY_CARDS = [
  { key: "faith",      label: "FÉ & DÍZIMO",         Icon: Heart,         sub: "Dízimo (Mensal)",     value: "285",   unit: "€", note: "10% do rendimento",  pct: 100, accent: "#0A9B5F" },
  { key: "family",     label: "SEGURANÇA FAMILIAR",  Icon: ShieldCheck,   sub: "Este Mês",            value: "1.250", unit: "€", note: "76% das despesas",   pct: 76,  accent: "#0A9B5F" },
  { key: "emergency",  label: "RESERVA DE EMERGÊNCIA", Icon: PiggyBank,   sub: "do Alvo de 5.400 €",  value: "2.650", unit: "€", note: "49%",                pct: 49,  accent: "#0A9B5F" },
  { key: "growth",     label: "CRESCIMENTO PESSOAL", Icon: GraduationCap, sub: "Este Mês",            value: "320",   unit: "€", note: "19% das despesas",   pct: 60,  accent: "#EAB308" },
  { key: "experiences",label: "EXPERIÊNCIAS FAMILIARES", Icon: Plane,    sub: "Fundo de Viagens",    value: "950",   unit: "€", note: "63% de 1.500 €",     pct: 63,  accent: "#0A9B5F" },
  { key: "hx",         label: "HX ECOSYSTEMS",       Icon: Briefcase,     sub: "Este Mês",            value: "1.250", unit: "€", note: "44% do rendimento",  pct: 80,  accent: "#0A9B5F" },
];

export const SHIFTS = [
  { label: "Turno Manhã",  range: "06:00 - 14:00", count: 3, hours: 24, income: 480, Icon: Sunrise, color: "#0A9B5F" },
  { label: "Turno Diurno", range: "14:00 - 22:00", count: 2, hours: 16, income: 320, Icon: Sun,     color: "#EAB308" },
  { label: "Turno Tarde",  range: "22:00 - 06:00", count: 1, hours:  8, income: 160, Icon: Sunset,  color: "#F97316" },
  { label: "Turno Noite",  range: "00:00 - 08:00", count: 2, hours: 16, income: 320, Icon: Moon,    color: "#0A9B5F" },
];

export const HX_INVESTMENTS = [
  { label: "HX Smart Point",        v: 500, p: 60 },
  { label: "MegaMix",               v: 250, p: 40 },
  { label: "OBÔGO",                 v: 200, p: 35 },
  { label: "MIONGA",                v: 150, p: 30 },
  { label: "Desenvolvimento Web",   v: 100, p: 50 },
  { label: "Marketing",             v:  50, p: 25 },
];

/* ===================== Sidebar nav ===================== */
export const NAV_PUBLIC = [
  { id: "home",       label: "Início",                Icon: Home },
  { id: "philosophy", label: "Filosofia",             Icon: BookOpen },
  { id: "overview",   label: "Visão do Sistema",      Icon: LayoutGrid },
  { id: "demo",       label: "Painel Demo",           Icon: ChartArea },
  { id: "goals-ex",   label: "Objetivos (Exemplo)",   Icon: Target },
  { id: "roadmap-ex", label: "Roadmap (Exemplo)",     Icon: Map },
  { id: "about",      label: "Sobre HX Ecosystems",   Icon: Info },
];

export const NAV_PRIVATE = [
  { id: "dashboard",   label: "Painel (Privado)",       Icon: LayoutGrid },
  { id: "faith",       label: "Fé & Dízimo",            Icon: Heart },
  { id: "family",      label: "Segurança Familiar",     Icon: ShieldCheck },
  { id: "emergency",   label: "Reserva de Emergência",  Icon: PiggyBank },
  { id: "growth",      label: "Crescimento Pessoal",    Icon: GraduationCap },
  { id: "ausbildung",  label: "Plano Ausbildung",       Icon: Briefcase },
  { id: "driving",     label: "Plano Carta de Condução", Icon: Car },
  { id: "experiences", label: "Experiências Familiares", Icon: Plane },
  { id: "hx",          label: "HX Ecosystems",          Icon: TrendingUp },
  { id: "notes",       label: "Notas & Lembretes",      Icon: ClipboardList },
  { id: "goals",       label: "Objetivos & Marcos",     Icon: Target },
  { id: "legacy",      label: "Construtor de Legado",   Icon: Trophy },
  { id: "reports",     label: "Relatórios & Analítica", Icon: BarChart3 },
  { id: "settings",    label: "Definições",             Icon: Settings },
];

/* ===================== Weekly shift schedule ===================== */
export const SHIFT_TYPES = [
  { id: "morning", label: "Manhã",  range: "06:00 - 14:00", hours: 8, income: 160, color: "#0A9B5F", Icon: Sunrise },
  { id: "day",     label: "Diurno", range: "14:00 - 22:00", hours: 8, income: 160, color: "#EAB308", Icon: Sun },
  { id: "evening", label: "Tarde",  range: "22:00 - 06:00", hours: 8, income: 160, color: "#F97316", Icon: Sunset },
  { id: "night",   label: "Noite",  range: "00:00 - 08:00", hours: 8, income: 160, color: "#0A9B5F", Icon: Moon },
  { id: "off",     label: "Folga",  range: "—",             hours: 0, income: 0,   color: "#6B7280", Icon: Circle },
];
export const SHIFT_BY_ID = SHIFT_TYPES.reduce((m, s) => (m[s.id] = s, m), {});

export const DAYS_WEEK = [
  { id: "mon", short: "Seg", long: "Segunda-feira" },
  { id: "tue", short: "Ter", long: "Terça-feira" },
  { id: "wed", short: "Qua", long: "Quarta-feira" },
  { id: "thu", short: "Qui", long: "Quinta-feira" },
  { id: "fri", short: "Sex", long: "Sexta-feira" },
  { id: "sat", short: "Sáb", long: "Sábado" },
  { id: "sun", short: "Dom", long: "Domingo" },
];

export const DEMO_WEEK = {
  mon: "morning", tue: "morning", wed: "day",
  thu: "morning", fri: "night", sat: "evening", sun: "off",
};

/* ===================== Centro de Comando (top row) ===================== */
export const COMMAND_CARDS = [
  { key: "mission_month",    label: `Missão · ${CURRENT_MONTH_LABEL}`,  Icon: Target,    placeholder: "Define a tua missão deste mês",         editable: true,  deriveFrom: null },
  { key: "main_priority",    label: "Principal Prioridade",   Icon: Flag,      placeholder: "—",                                     editable: false, deriveFrom: "priority" },
  { key: "next_goal",        label: "Próximo Objetivo",       Icon: Star,      placeholder: "—",                                     editable: false, deriveFrom: "goal" },
  { key: "next_meeting",     label: "Próxima Reunião",        Icon: Users,     placeholder: "—",                                     editable: false, deriveFrom: "meeting" },
  { key: "next_payment",     label: "Próximo Pagamento",      Icon: Receipt,   placeholder: "—",                                     editable: true,  deriveFrom: "payment" },
  { key: "next_milestone",   label: "Próximo Marco",          Icon: Sparkles,  placeholder: "—",                                     editable: false, deriveFrom: "milestone" },
  { key: "overall_progress", label: "Progresso Geral",        Icon: TrendingUp,placeholder: "—",                                     editable: false, deriveFrom: "progress" },
];

export const deriveCommandValue = (deriveFrom, entriesByCat, roadmapSteps) => {
  if (!deriveFrom) return { value: null, sub: null };
  if (deriveFrom === "priority") {
    const list = roadmapSteps || ROADMAP;
    const next = list.find((r) => r.next) || list.find((r) => !r.done);
    return { value: next ? `${next.n}. ${next.label}` : "✓ Todas as etapas concluídas", sub: next?.status || null };
  }
  if (deriveFrom === "goal") {
    const list = entriesByCat?.goals || [];
    if (!list.length) return { value: "—", sub: null };
    const sorted = [...list].sort((a, b) => {
      const da = a.due_date || "9999-12-31";
      const dbb = b.due_date || "9999-12-31";
      return da.localeCompare(dbb);
    });
    const g = sorted[0];
    return { value: g.label, sub: g.due_date ? `prazo ${formatPtDate(g.due_date)}` : `${g.progress ?? 0}%` };
  }
  if (deriveFrom === "meeting") {
    const list = (entriesByCat?.notes || []).filter((n) => (n.note || "").toLowerCase().includes("reuni"));
    const next = [...list].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))[0];
    return next ? { value: next.label, sub: next.due_date ? formatPtDate(next.due_date) : null } : { value: "—", sub: null };
  }
  if (deriveFrom === "payment") {
    const list = (entriesByCat?.notes || []).filter((n) => (n.note || "").toLowerCase().includes("pagamento") || (n.label || "").toLowerCase().includes("pagamento"));
    const next = [...list].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))[0];
    return next ? { value: next.label, sub: next.due_date ? formatPtDate(next.due_date) : null } : { value: "—", sub: null };
  }
  if (deriveFrom === "milestone") {
    const list = entriesByCat?.legacy || [];
    const open = list.filter((l) => l.status !== "done");
    const next = open.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))[0];
    return next ? { value: next.label, sub: next.due_date ? formatPtDate(next.due_date) : null } : { value: "—", sub: null };
  }
  if (deriveFrom === "progress") {
    const goals = entriesByCat?.goals || [];
    if (!goals.length) return { value: "—", sub: null };
    const avg = Math.round(goals.reduce((s, g) => s + (g.progress ?? 0), 0) / goals.length);
    return { value: `${avg}%`, sub: `${goals.length} objetivos ativos` };
  }
  return { value: null, sub: null };
};

/* ===================== Construtor de Legado ===================== */
export const LEGACY_TEMPLATES = [
  { label: "Fundo de Emergência Completo",     Icon: PiggyBank },
  { label: "Alemão B1 Concluído",              Icon: GraduationCap },
  { label: "Alemão B2 Concluído",              Icon: GraduationCap },
  { label: "Ausbildung Iniciada",              Icon: Briefcase },
  { label: "Carta de Condução Obtida",         Icon: Car },
  { label: "HX Smart Point Lançado",           Icon: TrendingUp },
  { label: "Primeira Parceria Estratégica",    Icon: Users },
  { label: "Primeira Expansão Internacional",  Icon: Globe },
];

/* ===================== Distribuição de Missão ===================== */
export const MISSION_BREAKDOWN = [
  { key: "faith",      label: "Dízimo",       pct: 10, color: "#0A9B5F", Icon: Heart },
  { key: "family",     label: "Família",      pct: 40, color: "#0A9B5F", Icon: ShieldCheck },
  { key: "emergency",  label: "Reserva",      pct: 20, color: "#EAB308", Icon: PiggyBank },
  { key: "growth",     label: "Crescimento",  pct: 15, color: "#A855F7", Icon: GraduationCap },
  { key: "hx",         label: "HX Ecosystems", pct: 15, color: "#F97316", Icon: Briefcase },
];

/* ===================== Category sheet definitions ===================== */
export const SHEET_MAP = {
  faith: {
    title: "Faith & Giving",
    icon: Heart,
    description: "Dízimo, doações, contribuições. O fundamento espiritual antes de qualquer plano.",
    columns: ["label", "amount", "monthly", "due_date", "note"],
  },
  family: {
    title: "Family Security",
    icon: ShieldCheck,
    description: "Casa, comida, escola, saúde, transporte. As despesas essenciais protegidas mensalmente.",
    columns: ["label", "amount", "monthly", "note"],
  },
  emergency: {
    title: "Emergency Reserve",
    icon: PiggyBank,
    description: "6 meses de despesa familiar em reserva líquida. Antes de qualquer expansão.",
    columns: ["label", "amount", "target", "monthly", "progress"],
  },
  growth: {
    title: "Personal Growth",
    icon: GraduationCap,
    description: "Alemão B1/B2, certificações, livros, formação contínua. Skill compounding.",
    columns: ["label", "amount", "target", "progress", "due_date", "note"],
  },
  driving: {
    title: "Driving License Plan",
    icon: Car,
    description: "Inscrição, teoria, prática, taxas de exame. Liberdade de mobilidade familiar.",
    columns: ["label", "amount", "target", "monthly", "progress", "note"],
  },
  experiences: {
    title: "Family Experiences",
    icon: Plane,
    description: "Viagens, férias, atividades familiares. Memórias que valem mais que dinheiro.",
    columns: ["label", "amount", "target", "monthly", "due_date", "note"],
  },
  hx: {
    title: "HX Ecosystems Investments",
    icon: TrendingUp,
    description: "HX Smart Point, MegaMix, OBÔGO, MIONGA, website, marketing, parcerias estratégicas.",
    columns: ["label", "amount", "target", "progress", "note"],
  },
  shifts: {
    title: "Work Shift Planning (legacy)",
    icon: Calendar,
    description: "Vista antiga substituída pela Escala Semanal no painel principal.",
    columns: ["label", "amount", "target", "monthly", "note"],
  },
  notes: {
    title: "Notes & Reminders",
    icon: ClipboardList,
    description: "Reuniões, prazos, datas de pagamento, marcos importantes.",
    columns: ["label", "due_date", "note"],
  },
  goals: {
    title: "Goals & Milestones",
    icon: Target,
    description: "Objetivos quantificáveis com progresso. Família, idioma, Ausbildung, HX, longo prazo.",
    columns: ["label", "amount", "target", "progress", "due_date", "note"],
  },
  legacy: {
    title: "Construtor de Legado",
    icon: Trophy,
    description: "Marcos transformados em conquistas permanentes. Fundo de emergência, B1/B2, Ausbildung, carta, HX, expansão.",
    columns: ["label", "progress", "due_date", "note"],
  },
};

export const ausbildungSheet = { ...SHEET_MAP.growth, title: "Ausbildung Plan", description: "Formação profissional certificada. Caminho para a qualificação no mercado alemão." };

export const ALL_PRIVATE_VIEWS = new Set([
  "dashboard", "faith", "family", "emergency", "growth", "ausbildung",
  "driving", "experiences", "hx", "shifts", "notes", "goals", "reports", "settings",
]);

/* ===================== Misc visuals ===================== */
export const COLORS_LIST = ["#0A9B5F", "#0A9B5F", "#F97316", "#EAB308", "#A855F7", "#06B6D4", "#EF4444"];
export const SHIFT_VISUALS = [
  { Icon: Sunrise, color: "#0A9B5F" },
  { Icon: Sun,     color: "#EAB308" },
  { Icon: Sunset,  color: "#F97316" },
  { Icon: Moon,    color: "#0A9B5F" },
];

/* ===================== Format helpers ===================== */
export const fmtEuro = (n) => `${new Intl.NumberFormat("de-DE").format(n)} €`;

/* ===================== Builders (real state with demo fallback) ===================== */
export const buildKpiList = (kpisStore, entriesByCat) => {
  const income     = kpisStore?.monthly_income?.amount    ?? 2850;
  const expenses   = kpisStore?.total_expenses?.amount    ?? 1650;
  const reserve    = kpisStore?.emergency_reserve?.amount ?? 2650;
  const reserveTgt = kpisStore?.emergency_target?.amount  ?? 5400;
  const hxFromEntries = (entriesByCat?.hx || []).reduce((s, e) => s + (e.amount || 0), 0);
  const hxInvested = hxFromEntries > 0 ? hxFromEntries : (kpisStore?.hx_invested?.amount ?? 1250);
  const savingsStored = kpisStore?.savings_rate?.amount;
  const savings = savingsStored != null
    ? savingsStored
    : (income > 0 ? Math.round(((income - expenses) / income) * 100) : 0);

  const expensesPct = income > 0 ? Math.round((expenses / income) * 100) : 0;
  const reservePct  = reserveTgt > 0 ? Math.round((reserve / reserveTgt) * 100) : 0;
  const hxPct       = income > 0 ? Math.round((hxInvested / income) * 100) : 0;
  const savingsLbl  = savings >= 30 ? "Excelente" : savings >= 20 ? "Bom" : savings >= 10 ? "Atenção" : "Crítico";

  const fmtN = (n) => new Intl.NumberFormat("de-DE").format(n);

  return [
    { key: "monthly_income",    label: "Rendimento Mensal",          rawValue: income,    value: fmtN(income),    unit: "€", sub: "Após Impostos",                 delta: "Líquido mensal",                Icon: Wallet,     accent: "neon" },
    { key: "total_expenses",    label: "Despesa Total",              rawValue: expenses,  value: fmtN(expenses),  unit: "€", sub: "Este Mês",                       delta: `${expensesPct}% do rendimento`, Icon: Receipt,    accent: "red"  },
    { key: "savings_rate",      label: "Taxa de Poupança",           rawValue: savings,   value: fmtN(savings),   unit: "%", sub: "Este Mês",                       delta: savingsLbl,                      Icon: PiggyBank,  accent: "neon" },
    { key: "emergency_reserve", label: "Reserva de Emergência",      rawValue: reserve,   value: fmtN(reserve),   unit: "€", sub: `do Alvo de ${fmtN(reserveTgt)} €`, delta: `${reservePct}% Concluído`,    Icon: ShieldCheck,accent: "neon" },
    { key: "hx_invested",       label: "Investido em HX Ecosystems", rawValue: hxInvested,value: fmtN(hxInvested),unit: "€", sub: "Este Mês",                       delta: `${hxPct}% do rendimento`,       Icon: TrendingUp, accent: "neon" },
  ];
};

export const buildCategoryCards = (entriesByCat, kpisStore) => {
  const income     = kpisStore?.monthly_income?.amount    ?? 2850;
  const expenses   = kpisStore?.total_expenses?.amount    ?? 1650;
  const reserve    = kpisStore?.emergency_reserve?.amount ?? 2650;
  const reserveTgt = kpisStore?.emergency_target?.amount  ?? 5400;
  const travelTgt  = kpisStore?.travel_target?.amount     ?? 1500;

  const sumCat = (cat, demo) => {
    const list = entriesByCat?.[cat] || [];
    const sum = list.reduce((s, e) => s + (e.amount || 0), 0);
    return sum > 0 ? sum : demo;
  };

  const faithVal      = sumCat("faith",       285);
  const familyVal     = sumCat("family",      1250);
  const growthVal     = sumCat("growth",      320);
  const experiencesVal= sumCat("experiences", 950);
  const hxFromEntries = (entriesByCat?.hx || []).reduce((s, e) => s + (e.amount || 0), 0);
  const hxVal         = hxFromEntries > 0 ? hxFromEntries : (kpisStore?.hx_invested?.amount ?? 1250);

  const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
  const fmtN = (n) => new Intl.NumberFormat("de-DE").format(n);
  const cap100 = (v) => Math.min(100, v);

  return [
    { key: "faith",      label: "FÉ & DÍZIMO",             Icon: Heart,         sub: "Dízimo (Mensal)",         rawValue: faithVal,      value: fmtN(faithVal),      unit: "€", note: `${pct(faithVal, income)}% do rendimento`, pct: cap100(pct(faithVal, income) * 10),     accent: "#0A9B5F" },
    { key: "family",     label: "SEGURANÇA FAMILIAR",      Icon: ShieldCheck,   sub: "Este Mês",                rawValue: familyVal,     value: fmtN(familyVal),     unit: "€", note: `${pct(familyVal, expenses)}% das despesas`, pct: cap100(pct(familyVal, expenses)),      accent: "#0A9B5F" },
    { key: "emergency",  label: "RESERVA DE EMERGÊNCIA",   Icon: PiggyBank,     sub: `do Alvo de ${fmtN(reserveTgt)} €`, rawValue: reserve, value: fmtN(reserve),     unit: "€", note: `${pct(reserve, reserveTgt)}%`,             pct: cap100(pct(reserve, reserveTgt)),      accent: "#0A9B5F" },
    { key: "growth",     label: "CRESCIMENTO PESSOAL",     Icon: GraduationCap, sub: "Este Mês",                rawValue: growthVal,     value: fmtN(growthVal),     unit: "€", note: `${pct(growthVal, expenses)}% das despesas`, pct: cap100(pct(growthVal, expenses) * 3),  accent: "#EAB308" },
    { key: "experiences",label: "EXPERIÊNCIAS FAMILIARES", Icon: Plane,         sub: "Fundo de Viagens",        rawValue: experiencesVal,value: fmtN(experiencesVal),unit: "€", note: `${pct(experiencesVal, travelTgt)}% de ${fmtN(travelTgt)} €`, pct: cap100(pct(experiencesVal, travelTgt)), accent: "#0A9B5F" },
    { key: "hx",         label: "HX ECOSYSTEMS",           Icon: Briefcase,     sub: "Este Mês",                rawValue: hxVal,         value: fmtN(hxVal),         unit: "€", note: `${pct(hxVal, income)}% do rendimento`,     pct: cap100(pct(hxVal, income) * 2),         accent: "#0A9B5F" },
  ];
};

export const buildReminders = (notes) => notes.length > 0
  ? notes.map((n, i) => ({ id: n.id, label: n.label, date: n.due_date || "—", color: COLORS_LIST[i % COLORS_LIST.length] }))
  : REMINDERS;

export const buildGoals = (entries) => entries.length > 0
  ? entries.map((g) => ({ id: g.id, label: g.label, pct: g.progress ?? (g.target ? Math.round(((g.amount || 0) / g.target) * 100) : 0) }))
  : TOP_GOALS;

export const buildInvestments = (entries) => entries.length > 0
  ? entries.map((e) => ({ id: e.id, label: e.label, v: e.amount ?? 0, p: e.progress ?? 0 }))
  : HX_INVESTMENTS;

export const buildShifts = (entries) => entries.length > 0
  ? entries.map((e, i) => {
      const vis = SHIFT_VISUALS[i % SHIFT_VISUALS.length];
      return { id: e.id, label: e.label, range: e.note || "—", count: e.amount ?? 0, hours: e.target ?? 0, income: e.monthly ?? 0, Icon: vis.Icon, color: vis.color };
    })
  : SHIFTS;
